import { useRef, useState, useEffect } from "react";
import type { Vote } from "../../../api/types";
import { basicInfoStore } from "../utils/basicInfoStore";

interface Props {
  vote: Vote;
  onClose: () => void;
}

export default function VoteReportModal({ vote, onClose }: Props) {
  const printRef = useRef<HTMLDivElement>(null);
  const [printing, setPrinting] = useState(false);
  const [activeTab, setActiveTab] = useState<"report" | "voters">("report");
  const [voterSearch, setVoterSearch] = useState("");
  const [orgLine, setOrgLine] = useState(() => (basicInfoStore.get().shortName || basicInfoStore.get().name || "协会").trim() || "协会");

  useEffect(() => {
    return basicInfoStore.subscribe(() => {
      const b = basicInfoStore.get();
      setOrgLine((b.shortName || b.name || "协会").trim() || "协会");
    });
  }, []);

  const total = vote.options.reduce((s, o) => s + o.votes, 0);
  const sorted = [...vote.options].sort((a, b) => b.votes - a.votes);
  const winner = sorted[0];
  const today = new Date().toLocaleDateString("zh-CN", { year: "numeric", month: "long", day: "numeric" });
  const hasImages = vote.options.some((o) => o.imageUrl);

  // Voters tab data
  const participants = vote.participants ?? [];
  const filteredVoters = participants.filter((p) =>
    !voterSearch.trim() ||
    p.name.includes(voterSearch) ||
    p.memberId.toLowerCase().includes(voterSearch.toLowerCase())
  );
  const optionMap = Object.fromEntries(vote.options.map((o) => [o.id, o.text]));

  const handlePrint = () => {
    setPrinting(true);
    const printContent = printRef.current?.innerHTML ?? "";
    const win = window.open("", "_blank", "width=900,height=700");
    if (!win) { setPrinting(false); return; }
    win.document.write(`<!DOCTYPE html><html><head>
      <meta charset="UTF-8">
      <title>${vote.title} - 投票结果报告</title>
      <style>
        *{margin:0;padding:0;box-sizing:border-box}
        body{font-family:"PingFang SC","Microsoft YaHei",sans-serif;color:#1f2937;background:#fff;padding:48px;max-width:794px;margin:0 auto}
        h1{font-size:20px;font-weight:700;margin-bottom:4px}
        h2{font-size:13px;font-weight:700;margin-bottom:12px;color:#374151;border-left:3px solid #ef4444;padding-left:10px}
        p{font-size:12px;color:#6b7280}
        .meta{font-size:11px;color:#9ca3af;margin-bottom:20px}
        .divider{border:none;border-top:1px solid #e5e7eb;margin:20px 0}
        .stat-row{display:flex;gap:12px;margin-bottom:20px}
        .stat-card{flex:1;background:#f9fafb;border-radius:8px;padding:12px;text-align:center}
        .stat-val{font-size:22px;font-weight:700;color:#111827}
        .stat-lbl{font-size:11px;color:#6b7280;margin-top:2px}
        .opt-row{margin-bottom:14px}
        .opt-header{display:flex;align-items:center;gap:10px;margin-bottom:6px}
        .rank{width:22px;height:22px;border-radius:50%;background:#f3f4f6;display:flex;align-items:center;justify-content:center;font-size:11px;font-weight:700;color:#6b7280;flex-shrink:0}
        .rank-1{background:#fef3c7;color:#d97706}
        .rank-2{background:#f3f4f6;color:#6b7280}
        .rank-3{background:#fef9e7;color:#92400e}
        .opt-text{font-size:13px;font-weight:600;color:#111827;flex:1}
        .opt-pct{font-size:13px;font-weight:700;color:#374151;flex-shrink:0}
        .bar-track{height:10px;background:#f3f4f6;border-radius:5px;overflow:hidden;margin-bottom:3px}
        .bar-fill{height:100%;background:#ef4444;border-radius:5px}
        .bar-fill-2{background:#fca5a5}
        .opt-desc{font-size:11px;color:#9ca3af;margin-top:3px}
        .opt-votes{font-size:11px;color:#9ca3af}
        .img-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(120px,1fr));gap:8px;margin-bottom:14px}
        .img-card{border-radius:8px;overflow:hidden;border:1px solid #e5e7eb}
        .img-card img{width:100%;height:80px;object-fit:cover}
        .img-card-body{padding:6px 8px}
        .img-card-title{font-size:11px;font-weight:600;color:#111827}
        .img-card-pct{font-size:11px;color:#ef4444;font-weight:700}
        .winner-box{background:#fef2f2;border:1px solid #fecaca;border-radius:8px;padding:14px;margin-bottom:20px;display:flex;align-items:center;gap:12px}
        .winner-icon{font-size:24px}
        .footer{margin-top:32px;padding-top:14px;border-top:1px solid #e5e7eb;display:flex;justify-content:space-between;font-size:10px;color:#9ca3af}
      </style>
    </head><body>${printContent}</body></html>`);
    win.document.close();
    win.focus();
    setTimeout(() => { win.print(); setPrinting(false); }, 500);
  };

  const barColor = (rank: number) => rank === 0 ? "#ef4444" : rank === 1 ? "#f87171" : "#d1d5db";

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[60] p-4">
      <div className="bg-gray-100 rounded-2xl flex flex-col w-full max-w-3xl max-h-[92vh] overflow-hidden">
        {/* Toolbar */}
        <div className="flex items-center justify-between px-5 py-3.5 bg-white border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 flex items-center justify-center bg-red-50 rounded-lg">
              <i className="ri-bar-chart-2-line text-red-600"></i>
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-800 truncate max-w-xs">{vote.title}</p>
              <p className="text-xs text-gray-400">生成于 {today}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {activeTab === "report" && (
              <button
                onClick={handlePrint}
                disabled={printing}
                className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white text-sm rounded-xl hover:bg-red-700 cursor-pointer transition-all whitespace-nowrap disabled:opacity-60"
              >
                <i className={printing ? "ri-loader-4-line" : "ri-printer-line"}></i>
                {printing ? "准备中..." : "打印 / 导出 PDF"}
              </button>
            )}
            <button onClick={onClose} className="w-8 h-8 flex items-center justify-center text-gray-500 hover:text-gray-700 cursor-pointer rounded-lg hover:bg-gray-100">
              <i className="ri-close-line text-lg"></i>
            </button>
          </div>
        </div>

        {/* Tab switcher */}
        <div className="flex border-b border-gray-200 bg-white flex-shrink-0 px-5">
          <button
            onClick={() => setActiveTab("report")}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap cursor-pointer ${activeTab === "report" ? "border-red-500 text-red-600" : "border-transparent text-gray-500 hover:text-gray-700"}`}
          >
            <i className="ri-bar-chart-2-line mr-1.5"></i>投票报告
          </button>
          <button
            onClick={() => setActiveTab("voters")}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap cursor-pointer flex items-center gap-1.5 ${activeTab === "voters" ? "border-red-500 text-red-600" : "border-transparent text-gray-500 hover:text-gray-700"}`}
          >
            <i className="ri-group-line"></i>
            投票人员
            <span className={`text-xs px-1.5 py-0.5 rounded-full font-bold ${activeTab === "voters" ? "bg-red-100 text-red-600" : "bg-gray-100 text-gray-500"}`}>{vote.totalVoters}</span>
          </button>
        </div>

        {/* Content */}
        {activeTab === "report" ? (
          /* Scrollable paper */
          <div className="flex-1 overflow-y-auto p-6 bg-gray-100">
            <div
              ref={printRef}
              style={{ width: "698px", minHeight: "900px", padding: "48px", fontFamily: "'PingFang SC','Microsoft YaHei',sans-serif", color: "#1f2937", background: "#fff", margin: "0 auto" }}
              className="rounded-xl"
            >
              {/* Header */}
              <div style={{ borderBottom: "2px solid #ef4444", paddingBottom: "14px", marginBottom: "20px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                  <div>
                    <h1 style={{ fontSize: "20px", fontWeight: "700", marginBottom: "4px" }}>投票结果报告</h1>
                    <p style={{ fontSize: "13px", color: "#374151", marginBottom: "2px" }}>{vote.title}</p>
                    <p style={{ fontSize: "11px", color: "#9ca3af" }}>
                      投票周期：{vote.startDate} ~ {vote.endDate} &nbsp;｜&nbsp; 生成时间：{today}
                    </p>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <p style={{ fontSize: "11px", color: "#9ca3af" }}>{orgLine}</p>
                    <p style={{ fontSize: "11px", color: "#9ca3af", marginTop: "3px" }}>内部报告</p>
                  </div>
                </div>
              </div>

              {/* Description */}
              {vote.description && (
                <p style={{ fontSize: "12px", color: "#6b7280", marginBottom: "18px", lineHeight: "1.7", padding: "10px 14px", background: "#f9fafb", borderRadius: "8px" }}>
                  {vote.description}
                </p>
              )}

              {/* Stats */}
              <div style={{ display: "flex", gap: "10px", marginBottom: "20px" }}>
                {[
                  { label: "总参与人数", value: `${vote.totalVoters} 人` },
                  { label: "选项总数",   value: `${vote.options.length} 项` },
                  { label: "总票数",     value: `${total} 票` },
                  { label: "投票方式",   value: `${vote.isAnonymous ? "匿名" : "实名"}${vote.allowMultiple ? " · 多选" : ""}` },
                ].map((s) => (
                  <div key={s.label} style={{ flex: 1, background: "#f9fafb", borderRadius: "8px", padding: "12px", textAlign: "center" }}>
                    <div style={{ fontSize: "18px", fontWeight: "700", color: "#111827" }}>{s.value}</div>
                    <div style={{ fontSize: "11px", color: "#6b7280", marginTop: "2px" }}>{s.label}</div>
                  </div>
                ))}
              </div>

              {/* Winner highlight */}
              {total > 0 && winner && (
                <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: "8px", padding: "14px", marginBottom: "20px", display: "flex", alignItems: "center", gap: "12px" }}>
                  <span style={{ fontSize: "22px" }}>🏆</span>
                  <div>
                    <p style={{ fontSize: "12px", color: "#9ca3af", marginBottom: "2px" }}>最高票数选项</p>
                    <p style={{ fontSize: "15px", fontWeight: "700", color: "#b91c1c" }}>{winner.text}</p>
                    <p style={{ fontSize: "11px", color: "#ef4444" }}>
                      {winner.votes} 票 · 占比 {Math.round((winner.votes / total) * 100)}%
                    </p>
                  </div>
                </div>
              )}

              <hr style={{ border: "none", borderTop: "1px solid #e5e7eb", margin: "20px 0" }} />

              {/* Results section */}
              <h2 style={{ fontSize: "13px", fontWeight: "700", marginBottom: "14px", color: "#374151", borderLeft: "3px solid #ef4444", paddingLeft: "10px" }}>
                各选项投票结果
              </h2>

              {/* Image grid if has images */}
              {hasImages && (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(130px,1fr))", gap: "8px", marginBottom: "16px" }}>
                  {sorted.map((opt, i) => {
                    const pct = total > 0 ? Math.round((opt.votes / total) * 100) : 0;
                    return (
                      <div key={opt.id} style={{ borderRadius: "8px", overflow: "hidden", border: `1px solid ${i === 0 ? "#fecaca" : "#e5e7eb"}`, background: i === 0 ? "#fef9f9" : "#fff" }}>
                        {opt.imageUrl && (
                          <img src={opt.imageUrl} alt={opt.text} style={{ width: "100%", height: "80px", objectFit: "cover" }} />
                        )}
                        <div style={{ padding: "7px 9px" }}>
                          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "4px" }}>
                            <span style={{ fontSize: "11px", fontWeight: "600", color: i === 0 ? "#b91c1c" : "#111827" }}>{opt.text}</span>
                            <span style={{ fontSize: "12px", fontWeight: "700", color: i === 0 ? "#ef4444" : "#6b7280" }}>{pct}%</span>
                          </div>
                          <div style={{ height: "5px", background: "#f3f4f6", borderRadius: "3px", overflow: "hidden" }}>
                            <div style={{ height: "100%", background: barColor(i), borderRadius: "3px", width: `${pct}%` }}></div>
                          </div>
                          <p style={{ fontSize: "10px", color: "#9ca3af", marginTop: "2px" }}>{opt.votes} 票</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Bar chart style list */}
              <div>
                {sorted.map((opt, i) => {
                  const pct = total > 0 ? Math.round((opt.votes / total) * 100) : 0;
                  return (
                    <div key={opt.id} style={{ marginBottom: "14px" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "5px" }}>
                        <div style={{
                          width: "22px", height: "22px", borderRadius: "50%",
                          background: i === 0 ? "#fef3c7" : i === 1 ? "#f3f4f6" : "#f9f9f9",
                          display: "flex", alignItems: "center", justifyContent: "center",
                          fontSize: "11px", fontWeight: "700",
                          color: i === 0 ? "#d97706" : "#6b7280",
                          flexShrink: 0,
                        }}>
                          {i + 1}
                        </div>
                        <span style={{ fontSize: "13px", fontWeight: "600", color: i === 0 ? "#111827" : "#374151", flex: 1 }}>{opt.text}</span>
                        <span style={{ fontSize: "13px", fontWeight: "700", color: i === 0 ? "#ef4444" : "#6b7280", flexShrink: 0 }}>
                          {opt.votes} 票 &nbsp; {pct}%
                        </span>
                      </div>
                      <div style={{ height: "10px", background: "#f3f4f6", borderRadius: "5px", overflow: "hidden", marginBottom: "3px" }}>
                        <div style={{ height: "100%", background: barColor(i), borderRadius: "5px", width: `${pct}%` }}></div>
                      </div>
                      {opt.description && (
                        <p style={{ fontSize: "11px", color: "#9ca3af", marginTop: "2px", paddingLeft: "32px" }}>{opt.description}</p>
                      )}
                    </div>
                  );
                })}
                {total === 0 && (
                  <p style={{ fontSize: "12px", color: "#9ca3af", textAlign: "center", padding: "20px" }}>暂无投票数据</p>
                )}
              </div>

              {/* Footer */}
              <div style={{ marginTop: "36px", paddingTop: "14px", borderTop: "1px solid #e5e7eb", display: "flex", justifyContent: "space-between", fontSize: "10px", color: "#9ca3af" }}>
                <span>{orgLine} · FanHub 协会管理系统</span>
                <span>本报告由系统自动生成，数据仅供内部参考</span>
                <span>{today}</span>
              </div>
            </div>
          </div>
        ) : (
          /* Voters Tab */
          <div className="flex-1 overflow-y-auto p-6 bg-gray-100">
            <div className="bg-white rounded-2xl overflow-hidden">
              {vote.isAnonymous ? (
                /* Anonymous notice */
                <div className="flex flex-col items-center justify-center py-16 px-8 text-center">
                  <div className="w-14 h-14 bg-gray-100 rounded-2xl flex items-center justify-center mb-4">
                    <i className="ri-shield-user-line text-gray-400 text-2xl"></i>
                  </div>
                  <p className="text-base font-semibold text-gray-700 mb-2">此投票为匿名投票</p>
                  <p className="text-sm text-gray-400 leading-relaxed max-w-xs">
                    匿名投票不记录投票人身份信息，仅统计各选项的票数，以保护投票者隐私。
                  </p>
                  <div className="mt-5 flex items-center gap-4 text-sm text-gray-500">
                    <span className="flex items-center gap-1.5"><i className="ri-group-line text-red-500"></i>共 <strong className="text-gray-800">{vote.totalVoters}</strong> 人参与</span>
                    <span className="flex items-center gap-1.5"><i className="ri-ticket-line text-red-500"></i>总票数 <strong className="text-gray-800">{total}</strong> 票</span>
                  </div>
                </div>
              ) : participants.length === 0 ? (
                /* No data */
                <div className="flex flex-col items-center justify-center py-16 text-center">
                  <i className="ri-group-line text-4xl text-gray-300 mb-3 block"></i>
                  <p className="text-sm text-gray-400">暂无投票人员记录</p>
                </div>
              ) : (
                <>
                  {/* Stats */}
                  <div className="grid grid-cols-[28px_1fr_1fr_1fr_100px] gap-x-3 px-5 py-2.5 bg-gray-50 text-xs font-semibold text-gray-500 border-b border-gray-100">
                    <span>#</span>
                    <span>会员号 / 姓名</span>
                    <span>所选选项</span>
                    <span>选项详情</span>
                    <span>投票时间</span>
                  </div>

                  {/* Search */}
                  <div className="px-5 py-3 border-b border-gray-100">
                    <div className="relative">
                      <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
                      <input
                        type="text"
                        placeholder="搜索姓名或会员号…"
                        value={voterSearch}
                        onChange={(e) => setVoterSearch(e.target.value)}
                        className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-red-400"
                      />
                    </div>
                  </div>

                  {/* Table header */}
                  <div className="grid grid-cols-[28px_1fr_1fr_1fr_100px] gap-x-3 px-5 py-2.5 bg-gray-50 text-xs font-semibold text-gray-500 border-b border-gray-100">
                    <span>#</span>
                    <span>会员号 / 姓名</span>
                    <span>所选选项</span>
                    <span>选项详情</span>
                    <span>投票时间</span>
                  </div>

                  {/* Rows */}
                  <div className="divide-y divide-gray-50 max-h-[400px] overflow-y-auto">
                    {filteredVoters.length === 0 ? (
                      <div className="text-center py-8 text-sm text-gray-400">未找到匹配结果</div>
                    ) : (
                      filteredVoters.map((p, i) => (
                        <div key={p.memberId} className="grid grid-cols-[28px_1fr_1fr_1fr_100px] gap-x-3 px-5 py-3 items-center hover:bg-gray-50/50 transition-colors">
                          <span className="text-xs text-gray-400">{i + 1}</span>
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-gray-800 truncate">{p.name}</p>
                            <p className="text-xs text-gray-400">{p.memberId}</p>
                          </div>
                          <div className="flex flex-wrap gap-1">
                            {p.votedOptions.map((oid) => (
                              <span key={oid} className="text-xs px-1.5 py-0.5 bg-red-50 text-red-600 rounded-md font-medium">{oid.toUpperCase()}</span>
                            ))}
                          </div>
                          <div className="min-w-0">
                            {p.votedOptions.map((oid) => (
                              <p key={oid} className="text-xs text-gray-600 truncate">{optionMap[oid] ?? oid}</p>
                            ))}
                          </div>
                          <span className="text-xs text-gray-400 whitespace-nowrap">{p.time}</span>
                        </div>
                      ))
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
